import { useCallback, useRef, useState } from "react";

export interface PropertyZoomContext {
  neighborhood: string | null;
  city: string | null;
  uf: string | null;
}

const STORAGE_KEY = "propertiesList_propertyZoomContext";

function readStoredContext(): PropertyZoomContext | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as PropertyZoomContext;
  } catch {
    return null;
  }
}

function writeStoredContext(context: PropertyZoomContext | null): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (!context) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  } catch {
    // sessionStorage may be unavailable
  }
}

export function usePropertyZoomReload() {
  const [pendingContext, setPendingContext] = useState<PropertyZoomContext | null>(
    () => readStoredContext()
  );
  const [shouldReloadOnZoom, setShouldReloadOnZoom] = useState(
    () => readStoredContext() !== null
  );
  const lastZoomRef = useRef<number | null>(null);

  const registerPropertyClick = useCallback(
    (context: PropertyZoomContext, mapZoom?: number) => {
      setPendingContext(context);
      setShouldReloadOnZoom(true);
      writeStoredContext(context);
      lastZoomRef.current =
        typeof mapZoom === "number" && Number.isFinite(mapZoom) ? mapZoom : null;
    },
    []
  );

  const consumeZoomReload = useCallback(
    (currentZoom: number): PropertyZoomContext | null => {
      const zoomChanged =
        lastZoomRef.current !== null && lastZoomRef.current !== currentZoom;

      lastZoomRef.current = currentZoom;

      if (!zoomChanged || !shouldReloadOnZoom || !pendingContext) {
        return null;
      }

      setShouldReloadOnZoom(false);
      setPendingContext(null);
      writeStoredContext(null);

      return pendingContext;
    },
    [pendingContext, shouldReloadOnZoom]
  );

  const resetZoomReload = useCallback(() => {
    setPendingContext(null);
    setShouldReloadOnZoom(false);
    writeStoredContext(null);
    lastZoomRef.current = null;
  }, []);

  return {
    registerPropertyClick,
    consumeZoomReload,
    resetZoomReload,
    hasPendingZoomReload: shouldReloadOnZoom,
  };
}
